'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { SearchApi, type ISuggestionItem, type IDramaInfo } from '@/apis/search';

import ClearSearchHistoryModal from './clear-search-history-modal';
import SearingContent from './content';
import { addSearchHistory, clearSearchHistory, getSearchHistory } from './search-history-storage';
import SearingHeader from './searing-header';

interface ISearingProps {
    onBack: () => void;
}

const KEYWORD_DEBOUNCE_MS = 300;
const PAGE_SIZE = 20;

export default function Searing(props: ISearingProps) {
    const { onBack } = props;
    const [keyword, setKeyword] = useState('');
    const [searchId, setSearchId] = useState('');
    const [loadingTitle, setLoadingTitle] = useState(false);
    const [titleList, setTitleList] = useState<ISuggestionItem[]>([]);
    const [loadingCards, setLoadingCards] = useState(false);
    const [selectedTitleId, setSelectedTitleId] = useState<string | null>(null);
    const [cardList, setCardList] = useState<IDramaInfo[]>([]);
    const [hasMore, setHasMore] = useState(false);
    const [searchHistoryKeywords, setSearchHistoryKeywords] = useState<string[]>([]);
    const [showClearHistoryModal, setShowClearHistoryModal] = useState(false);

    const debounceRef = useRef<NodeJS.Timeout | null>(null);
    const titleRequestIdRef = useRef(0);
    const cardRequestIdRef = useRef(0);
    // 用 ref 同步保存最新分页状态，避免回调读到过期 state
    const cursorRef = useRef('');
    const hasMoreRef = useRef(false);
    const loadingMoreRef = useRef(false);
    const selectedKeywordRef = useRef('');

    const isFetchingListRef = useRef(false);

    useEffect(() => {
        setSearchHistoryKeywords(getSearchHistory());
    }, []);

    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        if (isFetchingListRef.current) {
            return () => {
                isFetchingListRef.current = false;
            };
        }

        if (!keyword.trim()) {
            setTitleList([]);
            setCardList([]);
            setSelectedTitleId(null);
            setLoadingTitle(false);
        } else {
            setSearchId(Date.now().toString());
            setLoadingTitle(true);
            debounceRef.current = setTimeout(() => {
                const requestId = ++titleRequestIdRef.current;
                SearchApi.getSuggestList({
                    keyword: keyword.trim(),
                    size: 15,
                    highlight_tag: 'em',
                })
                    .then(res => {
                        if (titleRequestIdRef.current !== requestId) {
                            return;
                        }
                        const suggestions = res.data?.suggestions ?? [];
                        setTitleList(suggestions as unknown as ISuggestionItem[]);
                        setSelectedTitleId(null);
                        setCardList([]);
                    })
                    .catch(() => {
                        if (titleRequestIdRef.current !== requestId) {
                            return;
                        }
                        setTitleList([]);
                        setSelectedTitleId(null);
                        setCardList([]);
                    })
                    .finally(() => {
                        if (titleRequestIdRef.current === requestId) {
                            setLoadingTitle(false);
                        }
                    });
            }, KEYWORD_DEBOUNCE_MS);
        }

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [keyword]);

    const fetchDramaList = useCallback(async (kw: string, nextCursor: string, append: boolean) => {
        const requestId = ++cardRequestIdRef.current;
        if (append) {
            loadingMoreRef.current = true;
        } else {
            setLoadingCards(true);
        }
        try {
            const res = await SearchApi.getDramaList({
                keyword: kw,
                page_size: PAGE_SIZE,
                highlight_tag: 'em',
                cursor: nextCursor,
            });
            if (cardRequestIdRef.current !== requestId) {
                return;
            }
            const dramaList = res.data?.drama_list ?? [];
            const newCursor = res.data?.cursor ?? '';
            const newHasMore = Boolean(res.data?.has_next);
            setCardList(prev => (append ? [...prev, ...dramaList] : dramaList));
            setHasMore(newHasMore);
            cursorRef.current = newCursor;
            hasMoreRef.current = newHasMore;
        } catch (err) {
            if (cardRequestIdRef.current !== requestId) {
                return;
            }
            if (!append) {
                setCardList([]);
            }
            setHasMore(false);
            hasMoreRef.current = false;
            throw err;
        } finally {
            if (cardRequestIdRef.current === requestId && !append) {
                setLoadingCards(false);
            }
            if (append) {
                loadingMoreRef.current = false;
            }
        }
    }, []);

    /** 直接进入剧集列表搜索（跳过联想 debounce） */
    const runDirectDramaSearch = useCallback(
        (rawKw: string) => {
            const t = rawKw.trim();
            if (!t) {
                return;
            }
            setSelectedTitleId(t);
            selectedKeywordRef.current = t;
            cursorRef.current = '';
            hasMoreRef.current = false;
            setCardList([]);
            setHasMore(false);
            setKeyword(t);
            isFetchingListRef.current = true;
            fetchDramaList(t, '', false)
                .catch(() => {
                    // 错误已在 fetchDramaList 内部处理，这里仅吞掉外层 await 链
                })
                .finally(() => {});
        },
        [fetchDramaList]
    );

    const handleSelectTitle = (item: ISuggestionItem) => {
        const title = item.title || '';
        addSearchHistory(title);
        setSearchHistoryKeywords(getSearchHistory());
        runDirectDramaSearch(title);
    };

    const handleLoadMore = useCallback(async () => {
        if (!selectedKeywordRef.current || !hasMoreRef.current || loadingMoreRef.current) {
            return;
        }
        await fetchDramaList(selectedKeywordRef.current, cursorRef.current, true);
    }, [fetchDramaList]);

    const onSearch = (kw: string) => {
        const trimmed = kw.trim();
        if (!trimmed) {
            return;
        }
        addSearchHistory(trimmed);
        setSearchHistoryKeywords(getSearchHistory());
        runDirectDramaSearch(trimmed);
    };

    const handleSearchHistoryClear = () => {
        setShowClearHistoryModal(true);
    };

    const handleSearchHistoryClearCancel = () => {
        setShowClearHistoryModal(false);
    };

    const handleSearchHistoryClearConfirm = () => {
        clearSearchHistory();
        setSearchHistoryKeywords([]);
        setShowClearHistoryModal(false);
    };

    const handleSearchHistoryPick = (kw: string) => {
        runDirectDramaSearch(kw);
    };

    return (
        <div className='flex h-full min-h-0 flex-col overflow-hidden'>
            <SearingHeader keyword={keyword} onKeywordChange={setKeyword} onBack={onBack} onSearch={onSearch} />
            <div className='flex-1 overflow-y-auto'>
                <SearingContent
                    keyword={keyword}
                    searchId={searchId}
                    loadingTitle={loadingTitle}
                    titleList={titleList}
                    loadingCards={loadingCards}
                    hasMore={hasMore}
                    selectedTitleId={selectedTitleId}
                    cardList={cardList}
                    searchHistoryKeywords={searchHistoryKeywords}
                    onSelectTitle={handleSelectTitle}
                    onLoadMore={handleLoadMore}
                    onSearchHistoryClear={handleSearchHistoryClear}
                    onSearchHistoryPick={handleSearchHistoryPick}
                />
            </div>
            <ClearSearchHistoryModal
                visible={showClearHistoryModal}
                onCancel={handleSearchHistoryClearCancel}
                onConfirm={handleSearchHistoryClearConfirm}
            />
        </div>
    );
}
